import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';
import PageNavbar from './PageNavbar';
import { AuthContext } from '../context/AuthContext';
import './Caterers.css';

const formatInr = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

const Caterers = () => {
  const { user } = useContext(AuthContext);
  const canManage = ['Admin', 'Order Creator'].includes(user?.role);
  const [caterers, setCaterers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCaterer, setEditingCaterer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    supervisor_name: '',
    address: ''
  });

  const fetchCaterers = async () => {
    try {
      const response = await api.get('/caterers/list');
      setCaterers(response.data.caterers || []);
    } catch (error) {
      console.error('Error fetching caterers:', error);
      setCaterers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaterers();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', phone: '', supervisor_name: '', address: '' });
    setEditingCaterer(null);
    setShowForm(false);
  };

  const handleEdit = (caterer) => {
    setEditingCaterer(caterer);
    setFormData({
      name: caterer.name || '',
      phone: caterer.phone || '',
      supervisor_name: caterer.supervisor_name || '',
      address: caterer.address || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCaterer) {
        await api.put(`/caterers/${editingCaterer.id}`, formData);
        alert('Caterer updated successfully');
      } else {
        await api.post('/caterers', formData);
        alert('Caterer created successfully');
      }
      resetForm();
      fetchCaterers();
    } catch (error) {
      alert(error.response?.data?.error || `Error ${editingCaterer ? 'updating' : 'creating'} caterer`);
    }
  };

  const handleToggleActive = async (caterer) => {
    if (!window.confirm(`${caterer.active ? 'Deactivate' : 'Activate'} caterer "${caterer.name}"?`)) return;
    try {
      await api.put(`/caterers/${caterer.id}`, { active: !caterer.active });
      fetchCaterers();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update caterer');
    }
  };

  return (
    <div className="caterers-page">
      <PageNavbar />
      <div className="container">
        <div className="caterers-header">
          <div>
            <h1>Caterers</h1>
            <p className="caterers-hint" style={{ marginBottom: 0 }}>
              Open a caterer to see events, costs, dues, and statement of account.
            </p>
          </div>
          {canManage ? (
            <button
              className="btn btn-primary"
              onClick={() => {
                if (showForm) resetForm();
                else setShowForm(true);
              }}
            >
              {showForm ? 'Cancel' : 'Add Caterer'}
            </button>
          ) : null}
        </div>

        {showForm && canManage ? (
          <div className="card">
            <h2>{editingCaterer ? 'Edit Caterer' : 'Add New Caterer'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Caterer / party name"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="e.g. 1234567890"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Supervisor name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.supervisor_name}
                    onChange={(e) => setFormData({ ...formData, supervisor_name: e.target.value })}
                    placeholder="Supervisor name"
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Address"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="btn btn-primary">
                  {editingCaterer ? 'Update Caterer' : 'Add Caterer'}
                </button>
                {editingCaterer ? (
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </form>
          </div>
        ) : null}

        {loading ? (
          <div className="loading">Loading caterers...</div>
        ) : (
          <div className="card">
            <h2>All Caterers</h2>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Events</th>
                    <th>Billed</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {caterers.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="no-data">
                        No caterers yet.
                      </td>
                    </tr>
                  ) : (
                    caterers.map((caterer) => (
                      <tr key={caterer.id} className={caterer.active ? '' : 'caterer-inactive'}>
                        <td>
                          <strong>{caterer.name}</strong>
                          {caterer.supervisor_name ? (
                            <div className="muted">{caterer.supervisor_name}</div>
                          ) : null}
                        </td>
                        <td>{caterer.phone || '—'}</td>
                        <td>
                          {caterer.event_count || 0}
                          {caterer.open_count ? (
                            <div className="muted">{caterer.open_count} open</div>
                          ) : null}
                        </td>
                        <td>{formatInr(caterer.total_billed || 0)}</td>
                        <td className={caterer.total_due > 0 ? 'due-amount' : ''}>
                          {formatInr(caterer.total_due || 0)}
                        </td>
                        <td>
                          <span className={`status-badge ${caterer.active ? 'status-active' : 'status-inactive'}`}>
                            {caterer.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="caterer-actions">
                          <Link className="btn btn-sm btn-primary" to={`/caterers/${caterer.id}/account`}>
                            Events &amp; SOA
                          </Link>
                          {canManage ? (
                            <>
                              <button type="button" className="btn btn-sm btn-secondary" onClick={() => handleEdit(caterer)}>
                                Edit
                              </button>
                              <button
                                type="button"
                                className={`btn btn-sm ${caterer.active ? 'btn-secondary' : 'btn-success'}`}
                                onClick={() => handleToggleActive(caterer)}
                              >
                                {caterer.active ? 'Deactivate' : 'Activate'}
                              </button>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Caterers;
